import React, { useState, useEffect } from "react";
import {
  ArrowLeft,
  User,
  CheckCircle,
  XCircle,
  FileText,
  Filter,
  Loader2,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  getTeamLeaveRequests,
  approveLeaveRequest,
  rejectLeaveRequest,
} from "../../services/hrms.service";

const LeaveRequests = () => {
  const navigate = useNavigate();

  const [pendingLeaves, setPendingLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterType, setFilterType] = useState("all");
  const [actionLoading, setActionLoading] = useState(null); // Track which leave is being processed

  // Fetch team leave requests on component mount
  useEffect(() => {
    const fetchLeaveRequests = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await getTeamLeaveRequests(1, 10, "Pending");
        if (response.status === 1 && response.data) {
          // Transform API data to match component structure
          const formattedLeaves = response.data.map((leave) => ({
            id: leave.id,
            empName: leave.employee_name || `${leave.first_name || ""} ${leave.last_name || ""}`.trim() || "N/A",
            empId: leave.employee_id || "N/A",
            leaveType: leave.leave_type_name || leave.leave_type || "N/A",
            fromDate: leave.start_date ? new Date(leave.start_date).toLocaleDateString() : "N/A",
            toDate: leave.end_date ? new Date(leave.end_date).toLocaleDateString() : "N/A",
            days: leave.total_days || 1,
            reason: leave.reason || "",
            appliedOn: leave.created_at ? new Date(leave.created_at).toLocaleDateString() : "N/A",
          }));
          setPendingLeaves(formattedLeaves);
        } else {
          setPendingLeaves([]);
        }
      } catch (err) {
        console.error("Error fetching leave requests:", err);
        setError("Failed to load leave requests. Please try again.");
        setPendingLeaves([]);
      } finally {
        setLoading(false);
      }
    };

    fetchLeaveRequests();
  }, []);

  const handleApprove = async (leaveId) => {
    try {
      setActionLoading(leaveId);
      const response = await approveLeaveRequest(leaveId, "Approved by manager");
      if (response.status === 1) {
        // Remove the approved leave from the list
        setPendingLeaves(pendingLeaves.filter((leave) => leave.id !== leaveId));
        alert("Leave approved successfully!");
      } else {
        alert("Failed to approve leave. Please try again.");
      }
    } catch (error) {
      console.error("Error approving leave:", error);
      alert("Error approving leave. Please try again.");
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (leaveId) => {
    try {
      setActionLoading(leaveId);
      const response = await rejectLeaveRequest(leaveId, "Rejected by manager");
      if (response.status === 1) {
        // Remove the rejected leave from the list
        setPendingLeaves(pendingLeaves.filter((leave) => leave.id !== leaveId));
        alert("Leave rejected successfully!");
      } else {
        alert("Failed to reject leave. Please try again.");
      }
    } catch (error) {
      console.error("Error rejecting leave:", error);
      alert("Error rejecting leave. Please try again.");
    } finally {
      setActionLoading(null);
    }
  };

  const filteredLeaves =
    filterType === "all"
      ? pendingLeaves
      : pendingLeaves.filter((leave) => leave.leaveType === filterType);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-indigo-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading leave requests...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Error Loading Data</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 transition"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-12xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate("/hrms/manager-dashboard")}
            className="flex items-center text-indigo-600 hover:text-indigo-700 mb-4 font-medium"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Dashboard
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-800 mb-2">
                Leave Requests
              </h1>
              <p className="text-gray-600">
                Review and manage team leave requests
              </p>
            </div>
            <span className="bg-yellow-100 text-yellow-800 text-sm font-semibold px-4 py-2 rounded-full">
              {pendingLeaves.length} Pending
            </span>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
          <div className="flex items-center space-x-4">
            <Filter className="w-5 h-5 text-gray-400" />
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="all">All Leave Types</option>
              <option value="Sick Leave">Sick Leave</option>
              <option value="Casual Leave">Casual Leave</option>
              <option value="Earned Leave">Earned Leave</option>
            </select>
          </div>
        </div>

        {/* Leave Requests List */}
        <div className="space-y-4">
          {filteredLeaves.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm border p-12 text-center">
              <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">No pending leave requests</p>
            </div>
          ) : (
            filteredLeaves.map((leave) => (
              <div
                key={leave.id}
                className="bg-white rounded-lg shadow-sm border p-6 hover:shadow-md transition"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <User className="w-6 h-6 text-indigo-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800">
                        {leave.empName}
                      </h3>
                      <p className="text-sm text-gray-500">
                        Emp ID: {leave.empId} • Applied on {leave.appliedOn}
                      </p>
                    </div>
                  </div>
                  <span className="bg-yellow-100 text-yellow-800 text-xs font-medium px-3 py-1 rounded-full border border-yellow-300">
                    Pending
                  </span>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 bg-gray-50 p-4 rounded-lg">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Leave Type</p>
                    <p className="font-medium text-gray-800">
                      {leave.leaveType}
                    </p>
                  </div>
                  <div>
               <p className="text-xs text-gray-500 mb-1">From Date</p>
                    <p className="font-medium text-gray-800">
                      {leave.fromDate}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">To Date</p>
                    <p className="font-medium text-gray-800">{leave.toDate}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Total Days</p>
                    <p className="font-medium text-gray-800">
                      {leave.days} day{leave.days > 1 ? "s" : ""}
                    </p>
                  </div>
                </div>

                <div className="mb-4 bg-blue-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Reason</p>
                  <p className="text-sm text-gray-700">{leave.reason}</p>
                </div>

                <div className="flex space-x-3">
                  <button
                    onClick={() => handleApprove(leave.id)}
                    disabled={actionLoading === leave.id}
                    className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white font-medium py-3 px-4 rounded-lg transition flex items-center justify-center"
                  >
                    {actionLoading === leave.id ? (
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    ) : (
                      <CheckCircle className="w-5 h-5 mr-2" />
                    )}
                    {actionLoading === leave.id ? "Approving..." : "Approve"}
                  </button>
                  <button
                    onClick={() => handleReject(leave.id)}
                    disabled={actionLoading === leave.id}
                    className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white font-medium py-3 px-4 rounded-lg transition flex items-center justify-center"
                  >
                    {actionLoading === leave.id ? (
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    ) : (
                      <XCircle className="w-5 h-5 mr-2" />
                    )}
                    {actionLoading === leave.id ? "Rejecting..." : "Reject"}
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default LeaveRequests;